import { useContext, useEffect, useMemo, useState } from "react";
import TopNav from "ui/dashboard/topnav";
import Cookies from "js-cookie";
import { AuthContext, User } from "context/AuthContext";
import { Modal, Switch, Table } from "antd";
import {
  getUserForAdmin,
  loginForAdmin,
  activateUserForAdmin,
} from "./service";
import { useSearchParams, useLocation } from "react-router-dom";
import { showToast } from "ui/components/common";
import Search from "ui/search";
import type { TableColumnsType } from "antd";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import FreeLicense from "./freeLiscense";
import AiSwitch from "./aiSwitch";
import Limits from "pages/dashboard/usage/components/limits";
import { userRoleToDisplay } from "lib/utils";

type DataType = User & { firstName: string; lastName: string };

const classes = [
  { label: "Class A", value: "A", color: "rgba(50, 173, 230, 1)" },
  { label: "Class B", value: "B", color: "rgba(52, 199, 89, 1)" },
  { label: "Class C", value: "C", color: "rgba(0, 199, 190, 1)" },
];

export default function Page() {
  const { setUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [PageSize, setPageSize] = useState(20);
  const [isSettingAiLimitModalOpen, setIsSettingAiLimitModalOpen] =
    useState(false);
  const [orgId, setOrgId] = useState();
  const [users, setUsers] = useState<DataType[]>([]);
  const [freeLicenseModalVisible, setFreeLicenseModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const name = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("name") || "";
  }, [location]);

  const accountName = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("accountName") || "";
  }, [location]);
  const id = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("id") || "";
  }, [location]);
  const email = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("email") || "";
  }, [location]);
  useEffect(() => {
    if (current !== 0) {
      setCurrent(0);
      return;
    }
    getUserForAdmin(name, accountName, PageSize, current, id, email).then(
      (res) => {
        if (res?.status !== 200) {
          showToast(1, "Failed to get user list, please try again later");
          return;
        }
        const data = res.data;
        setUsers(data?.data);
        setTotalPage(data?.page?.total);
      },
    );
  }, [name, accountName, PageSize, id, email]);

  useEffect(() => {
    getUserForAdmin(name, accountName, PageSize, current, id, email).then(
      (res) => {
        if (res?.status !== 200) {
          showToast(1, "Failed to get user list, please try again later");
          return;
        }
        const data = res.data;
        setUsers(data?.data);
        setTotalPage(data?.page?.total);
      },
    );
  }, [current]);

  const activateUser = async (userId) => {
    const res = await activateUserForAdmin(userId);
    if (res?.status !== 200) {
      showToast(1, "Failed to activate user, please try again later");
      return;
    }
    showToast(0, "The activation email has been sent, please check it!");
  };

  const loginAsUser = async (userId) => {
    const res = await loginForAdmin(userId);
    if (res?.status !== 200) {
      showToast(1, "Failed to login as user, please try again later");
      return;
    }
    const token = res?.data.token;
    const accountName = res?.data.user.organization.name;
    Cookies.set("orgId", res?.data.user?.organization?.id, { expires: 1 });
    Cookies.set("userId", res?.data.user?.id, { expires: 1 });
    Cookies.set("token", token, {
      expires: 1,
      domain: "." + window.location.hostname,
    });
    Cookies.set("accountName", accountName, { expires: 1 });
    Cookies.set(
      "enableMonitoring",
      res?.data.user.organization.enableMonitoring,
      {
        expires: 1,
      },
    );
    Cookies.set(
      "name",
      res?.data.user.firstName + " " + res?.data.user.lastName,
    );
    Cookies.set("phoneNumber", res?.data.user.phoneNumber);
    window.parent.postMessage({ token: token, type: "loginClick" }, "*");
    const user: User = {
      id: res?.data.user.id,
      email: res?.data.user.email,
      accountName: res?.data.user.organization.name,
      orgId: res?.data.user.organization.id,
      role: res?.data.user.role.name,
      token: res?.data.token,
      name: res?.data.user.firstName + " " + res?.data.user.lastName,
      phoneNumber: res?.data.user.phoneNumber,
      organization: res?.data.user.organization,
    };

    setUser(user);

    navigate("/dashboard");
    window.location.reload();
  };
  function openFreeLicense(user) {
    setFreeLicenseModalVisible(true);
    setSelectedUser(user);
  }
  const columns: TableColumnsType<DataType> = [
    {
      title: "id",
      width: 200,
      render: (e) => <div>{e?.id}</div>,
    },
    {
      title: "Account Name",
      dataIndex: "accountName",
    },
    {
      title: "First Name",
      dataIndex: "firstName",
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Role",
      render: (e) => (
        <div className="font-semibold">{userRoleToDisplay(e?.role?.name)}</div>
      ),
    },
    {
      title: "Timezone",
      dataIndex: "timezone",
    },
    {
      title: "Status",
      dataIndex: "status",
    },
    {
      title: "Action",
      width: 280,
      render: (e) => (
        <div className="flex gap-2 flex-wrap">
          <button
            className={"text-blue-400 underline"}
            onClick={() => {
              if (e.status === "inactivate") {
                activateUser(e.id);
              } else {
                loginAsUser(e.id);
              }
            }}
          >
            {e.status === "inactivate" ? "Activate" : "Login"}
          </button>
          {e.role.name === "customer" && (
            <>
              <button
                className="text-blue-400 underline"
                onClick={() => {
                  setIsSettingAiLimitModalOpen(true);
                  setOrgId(e.organization.id);
                }}
              >
                AI Limit
              </button>
            </>
          )}
          {e.role.name === "customer" && (
            <>
              <button
                className="text-blue-400 underline"
                onClick={() => openFreeLicense(e)}
              >
                Free license
              </button>
              <AiSwitch data={e} />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="">
      <TopNav title="Users" />
      <div className="flex-grow pr-6 pb-6">
        <div className="w-full p-6 rounded-3xl bg-white">
          <div className="mb-4 flex gap-4">
            <div className="w-64">
              <Search placeholder="Search first or last name" query="name" />
            </div>
            <div className="w-64">
              <Search placeholder="Search account name" query="accountName" />
            </div>
            <div className="w-64">
              <Search placeholder="Id" query="id" />
            </div>
            <div className="w-64">
              <Search placeholder="Email" query="email" />
            </div>
          </div>
          <Table
            className="w-full pr-2"
            style={{ overflowX: "auto", maxHeight: "calc(100vh - 190px)" }}
            columns={columns}
            dataSource={users}
            pagination={{
              total: totalPage,
              onChange: (page) => setCurrent(page - 1),
              pageSize: PageSize,
              current: current + 1,
              onShowSizeChange: (_, size) => {
                setCurrent(0);
                setPageSize(size);
              },
            }}
            rowClassName={() => "no-row-border"}
          />
        </div>
      </div>
      <FreeLicense
        user={selectedUser}
        onClose={() => setFreeLicenseModalVisible(false)}
        visible={freeLicenseModalVisible}
      />
      <Modal
        title={<p className="text-lg font-semibold">AI Limit</p>}
        open={isSettingAiLimitModalOpen}
        centered
        footer={null}
        onCancel={() => {
          console.log("close");
          setIsSettingAiLimitModalOpen(false);
        }}
      >
        <Limits classes={classes} orgId={orgId} />
      </Modal>
    </div>
  );
}
